/* File: frontend/src/components/reputation/AIHealthWidget.js */
/* Version: 1.1 (Production Ready - With AbortController) */
/* Date: Monday, December 8, 2025 at 11:15 AM EST */
/* Description: AI Technical Health Widget. 
   Uses styles defined in ReputationDashboard.css (Section 14). */

import React, { useState, useEffect, useRef } from 'react';
import {
  Bot,
  ShieldCheck,
  ShieldAlert,
  FileJson,
  Link as LinkIcon,
  RefreshCw,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  ExternalLink
} from 'lucide-react';

// API Configuration
// Ensure this matches your main.py (/api/v1) and ai_tools.py (/ai-tools)
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api/v1';
const SCAN_ENDPOINT = `${API_BASE_URL}/ai-tools/scan`;

const AIHealthWidget = ({ targetUrl, autoRun = false }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  // Ref to cancel requests on unmount
  const abortControllerRef = useRef(null);

  // Auto-run logic
  useEffect(() => {
    if (autoRun && targetUrl && !data && !loading) {
      handleScan();
    }

    // Cleanup on unmount
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
    // eslint-disable-next-line
  }, [targetUrl, autoRun]);

  const handleScan = async () => {
    if (!targetUrl) return;

    // Cancel previous request if exists
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    setLoading(true);
    setError(null);
    setData(null); // Clear old data to show fresh start

    try {
      const response = await fetch(SCAN_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          // 'Authorization': `Bearer ${localStorage.getItem('token')}` 
        },
        body: JSON.stringify({ url: targetUrl }),
        signal: abortControllerRef.current.signal
      });

      // Check for HTML response (502/404 from proxy)
      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        throw new Error("Server returned non-JSON response. Check API URL.");
      }

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.detail || 'Scan failed');
      }

      setData(result);
    } catch (err) {
      if (err.name === 'AbortError') return; // Ignore abort errors

      console.error("AI Scan Error:", err);
      setError(err.message || "Network Error");
      setData(null); // Ensure no stale data on error
    } finally {
      // Only stop loading if component is still mounted (signal check)
      if (abortControllerRef.current && !abortControllerRef.current.signal.aborted) {
        setLoading(false);
      }
    }
  };

  // Helper Component for Status Icons
  const StatusIcon = ({ isSuccess, warning }) => {
    if (warning) return <AlertTriangle size={18} className="icon-yellow" />;
    return isSuccess ? (
      <CheckCircle2 size={18} color="#10b981" />
    ) : (
      <XCircle size={18} color="#f43f5e" />
    );
  };

  return (
    <div className="ai-health-widget">

      {/* --- Header --- */}
      <div className="ai-widget-header">
        <div className="ai-title-group">
          <h3>
            <Bot size={20} color="var(--primary-blue)" />
            AI Visibility Score
          </h3>
          <div className="bot-tags">
            <span className="bot-tag">GPTBot</span>
            <span className="bot-tag">Claude</span>
            <span className="bot-tag">Gemini</span>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          {targetUrl && (
            <a
              href={targetUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{ fontSize: '0.8rem', color: '#94a3b8', display: 'flex', alignItems: 'center', gap: '4px', textDecoration: 'none' }}
            >
              <ExternalLink size={12} />
              {new URL(targetUrl).hostname}
            </a>
          )}

          <button
            className="btn btn-primary btn-xs"
            onClick={handleScan}
            disabled={loading || !targetUrl}
          >
            <RefreshCw size={14} className={loading ? 'spin-anim' : ''} />
            {loading ? 'Scanning...' : (data ? 'Re-Scan' : 'Run Audit')}
          </button>
        </div>
      </div>

      {/* --- Error State --- */}
      {error && (
        <div style={{ padding: '1rem', background: '#fff1f2', color: '#be123c', fontSize: '0.9rem', borderBottom: '1px solid #fecdd3', display: 'flex', gap: '10px', alignItems: 'center' }}>
          <ShieldAlert size={18} />
          <div>
            <strong>Scan Failed:</strong> {error}
          </div>
        </div>
      )}

      {/* --- Empty State --- */}
      {!data && !loading && !error && (
        <div style={{ padding: '3rem', textAlign: 'center', color: '#64748b' }}>
          <div style={{ width: '48px', height: '48px', background: '#f1f5f9', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 15px auto' }}>
            <ShieldCheck size={24} color="#cbd5e1" />
          </div>
          <h4 style={{ margin: '0 0 5px 0', color: 'var(--dark-blue)' }}>System Ready</h4>
          <p style={{ margin: 0, fontSize: '0.9rem' }}>
            Click <strong>Run Audit</strong> to check if this content is visible to AI models.
          </p>
        </div>
      )}

      {/* --- Loading State --- */}
      {loading && !data && (
        <div style={{ padding: '3rem', textAlign: 'center', color: '#64748b' }}>
          <RefreshCw size={32} className="spin-anim" style={{ margin: '0 auto 15px auto', color: 'var(--primary-blue)' }} />
          <p>Analyzing robots.txt and Schema...</p>
        </div>
      )}

      {/* --- Results Grid --- */}
      {data && (
        <div className="ai-grid">

          {/* 1. Accessibility (Robots.txt) */}
          <div className="ai-col">
            <div className="ai-col-header">
              <span>
                <ShieldCheck size={14} style={{ marginRight: 5, verticalAlign: 'text-bottom' }} />
                Permissions
              </span>
              <StatusIcon isSuccess={data.accessibility.status !== 'error'} warning={data.accessibility.status === 'warning'} />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {data.accessibility.details.map((bot) => (
                <div key={bot.bot} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem' }}>
                  <span style={{ color: '#334155', fontWeight: 500 }}>{bot.bot}</span>
                  <span className={bot.allowed ? "badge-allowed" : "badge-blocked"}>
                    {bot.allowed ? 'ALLOWED' : 'BLOCKED'}
                  </span>
                </div>
              ))}
            </div>
            {data.accessibility.message && (
              <p style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: '12px', textAlign: 'center' }}>
                {data.accessibility.message}
              </p>
            )}
          </div>

          {/* 2. Integrity (Schema) */}
          <div className="ai-col">
            <div className="ai-col-header">
              <span>
                <FileJson size={14} style={{ marginRight: 5, verticalAlign: 'text-bottom' }} />
                Schema
              </span>
              <StatusIcon isSuccess={data.integrity.found} />
            </div>

            <div className="schema-stat-box">
              <span className="schema-count">{data.integrity.count || 0}</span>
              <span className="schema-label">Valid Objects</span>
            </div>

            <p style={{ fontSize: '0.8rem', textAlign: 'center', marginTop: '8px', fontWeight: 600, color: data.integrity.found ? '#059669' : '#e11d48' }}>
              {data.integrity.message}
            </p>
          </div>

          {/* 3. Connectivity (Orphans) */}
          <div className="ai-col">
            <div className="ai-col-header">
              <span>
                <LinkIcon size={14} style={{ marginRight: 5, verticalAlign: 'text-bottom' }} />
                Structure
              </span>
              <StatusIcon isSuccess={data.connectivity.linked} warning={data.connectivity.status === 'skipped'} />
            </div>

            <div className={`connectivity-box ${!data.connectivity.linked && data.connectivity.status !== 'skipped' ? 'warning' : ''} ${data.connectivity.status === 'skipped' ? 'skipped' : ''}`}>
              {data.connectivity.linked ? <LinkIcon size={24} /> : <XCircle size={24} />}
              <span style={{ fontWeight: '600', fontSize: '0.9rem', marginTop: '5px' }}>
                {data.connectivity.status === 'skipped'
                  ? 'Home Page'
                  : (data.connectivity.linked ? 'Linked' : 'Orphan Page')}
              </span>
            </div>

            <p style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: '10px', textAlign: 'center' }}>
              {data.connectivity.message}
            </p>
          </div>

        </div>
      )}
    </div>
  );
};

export default AIHealthWidget;